/**
 * Video Converter - Client-side HEVC/MOV to H.264 MP4 conversion
 * Uses ffmpeg.wasm (single-thread, no SharedArrayBuffer required)
 *
 * The goal: iPhone videos in HEVC codec cannot be played in Chrome/Firefox on PC.
 * We convert them in the admin/trainer's browser BEFORE uploading, so the server
 * always stores H.264 MP4 files that play everywhere.
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// Single-thread build (no SharedArrayBuffer needed) from unpkg CDN
const FFMPEG_CORE_VERSION = '0.12.6';
const FFMPEG_CORE_BASE = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

let _ffmpeg = null;
let _loadingPromise = null;

/**
 * Load (or return cached) ffmpeg instance.
 */
async function getFfmpeg(onLog) {
  if (_ffmpeg) return _ffmpeg;
  if (_loadingPromise) return _loadingPromise;

  _loadingPromise = (async () => {
    const ffmpeg = new FFmpeg();
    if (onLog) {
      ffmpeg.on('log', ({ message }) => {
        // Only log important messages to avoid console spam
        if (message && (message.includes('error') || message.includes('Stream') || message.includes('Duration'))) {
          onLog(message);
        }
      });
    }
    await ffmpeg.load({
      coreURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    _ffmpeg = ffmpeg;
    return ffmpeg;
  })();

  return _loadingPromise;
}

/**
 * Detect if a video file needs conversion by inspecting its bytes.
 * Returns true for HEVC/H.265 or unknown codecs that Chrome/Firefox can't play.
 *
 * We check the file container:
 *   - MP4/MOV with HEVC codec (hvc1, hev1) → NEEDS conversion
 *   - MP4 with H.264 (avc1) → NO conversion
 *   - QuickTime MOV → likely HEVC from iPhone → NEEDS conversion
 *   - Unknown/no extension → try conversion
 */
export async function needsConversion(file) {
  const name = (file.name || '').toLowerCase();
  const ext = name.split('.').pop() || '';

  // Already web-friendly: skip
  if (ext === 'webm') return false;

  // Read first 64KB to inspect container/codec
  const buf = await file.slice(0, 65536).arrayBuffer();
  const bytes = new Uint8Array(buf);
  const text = new TextDecoder('latin1').decode(bytes);

  // HEVC codec markers
  if (text.includes('hvc1') || text.includes('hev1') || text.includes('hvcC')) {
    return true;
  }

  // H.264 markers - safe to keep
  if (text.includes('avc1') || text.includes('avcC')) {
    return false;
  }

  // MOV from iPhone usually is HEVC when modern iOS. Convert to be safe.
  if (ext === 'mov' || ext === 'hevc' || ext === 'm4v') {
    return true;
  }

  // Default: no conversion for MP4 (assume H.264), convert others
  if (ext === 'mp4') return false;
  return true;
}

/**
 * Convert a video file to H.264 MP4 (web-compatible).
 * Progress callback receives 0-100.
 */
export async function convertToMp4(file, onProgress, onLog) {
  const ffmpeg = await getFfmpeg(onLog);

  const inputName = `input_${Date.now()}.${(file.name || 'vid.mov').split('.').pop() || 'mov'}`;
  const outputName = `output_${Date.now()}.mp4`;

  // Progress listener (0..1 from ffmpeg) → 0..100
  const progressHandler = ({ progress }) => {
    if (onProgress && typeof progress === 'number') {
      onProgress(Math.max(0, Math.min(100, Math.round(progress * 100))));
    }
  };
  ffmpeg.on('progress', progressHandler);

  try {
    await ffmpeg.writeFile(inputName, await fetchFile(file));

    // Convert to H.264/AAC, web-optimized (faststart for streaming)
    // -preset ultrafast keeps browser usage light; crf 28 keeps file small but acceptable
    await ffmpeg.exec([
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-crf', '28',
      '-pix_fmt', 'yuv420p',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      '-y',
      outputName,
    ]);

    const data = await ffmpeg.readFile(outputName);
    // Cleanup
    try { await ffmpeg.deleteFile(inputName); } catch (_) { /* ignore */ }
    try { await ffmpeg.deleteFile(outputName); } catch (_) { /* ignore */ }

    const blob = new Blob([data.buffer], { type: 'video/mp4' });
    const originalName = (file.name || 'video').replace(/\.[^.]+$/, '');
    return new File([blob], `${originalName}.mp4`, { type: 'video/mp4' });
  } finally {
    try { ffmpeg.off('progress', progressHandler); } catch (_) { /* ignore */ }
  }
}
